import React, { useEffect, useRef, useState } from "react";
import ProjectUsersTab from "./ProjectUsersTab";
import {
  UserInvitationCreate,
  OrganizationUserDto,
} from "../common/ObjectTypes";
import InviteUsersModal from "../common/InviteUsersModal";
import {
  createProjectUser,
  getProjectUsers,
  getProjectUserRoles,
  reinviteProjectUser,
  removeUserFromProject,
} from "../../api/projectsApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ProjectUserSearchQueryType } from "../common/QueryTypes";
import { pageSize } from "../../api/apiUtils";
import { ProjectRoles } from "../common/Enums";

type ManageProjectUsersProps = {
  projectId: number;
  projectName: string;
  perms: any[];
};

const ManageProjectUsers: React.FC<ManageProjectUsersProps> = ({
  projectId,
  projectName,
  perms,
}) => {
  const { t } = useTranslation();
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [isUserActionsModalOpen, setUserActionsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserDto>();
  const [activeUsers, setActiveUsers] = useState<OrganizationUserDto[]>([]);
  const [totalActiveUsersCount, setTotalActiveUsersCount] = useState(0);
  const [pendingUsers, setPendingUsers] = useState<OrganizationUserDto[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [inviteRoles, setInviteRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  /**
   * True while GET /projects/{id}/roles is in flight. Separate from `loading`,
   * which is the user table's spinner and clears as soon as the first user list
   * lands — well before the roles do on a slow API. The invite modal's Role
   * select reads this so it shows its loading state instead of "No options"
   * for an empty list that is merely not here yet (AKL-848, QA finding).
   */
  const [rolesLoading, setRolesLoading] = useState(true);
  /**
   * Generation counter for `loadRoles`. The effect that calls `fetchData`
   * re-runs on a project switch and on paging/filter changes, and the tab can
   * call it too, so two roles requests can be in flight at once. Only the
   * newest one may write `roles`/`inviteRoles` or lower `rolesLoading`;
   * otherwise a slow older request could clear the flag while the newer one
   * is still fetching, or land the previous project's roles on top of the
   * current one's.
   */
  const rolesRequest = useRef(0);
  const [inviteData, setInviteData] = useState<UserInvitationCreate>({
    email: "",
    role_id: 0,
  });
  const handleInviteUsers = () => {
    setInviteModalOpen(true);
  };
  const [sendingInvite, setSendingInvite] = useState(false);
  const [userSearchQuery, setUserSearchQuery] =
    useState<ProjectUserSearchQueryType>({
      proj_id: projectId,
      is_verified: true,
      first_name: "",
      last_name: "",
      email: "",
      role_id: undefined,
      take: pageSize,
      skip: localStorage.getItem("projectUsersTableSkip")
        ? parseInt(localStorage.getItem("projectUsersTableSkip") as string)
        : 0,
    });

  const loadActiveUsers = async (query: any) => {
    try {
      setLoading(true);
      const temp = await getProjectUsers(query);
      setActiveUsers(temp.users);
      setTotalActiveUsersCount(temp.count);
    } catch (err) {
      console.error("Error loading active users:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingUsers = async (query: any) => {
    try {
      setLoading(true);
      const temp = await getProjectUsers(query);
      setPendingUsers(temp.users);
    } catch (err) {
      console.error("Error loading pending users:", err);
    } finally {
      setLoading(false);
    }
  };

  const mapRolesToNameValue = (
    roles: any[],
  ): { name: string; value: any }[] => {
    return roles.map((role) => ({
      name: ProjectRoles[role.value], // Assuming role.value corresponds to the enum key
      value: role.value,
    }));
  };

  const loadRoles = async () => {
    const request = ++rolesRequest.current;
    const isLatest = () => request === rolesRequest.current;
    setRolesLoading(true);
    try {
      const temp = await getProjectUserRoles(projectId);
      // A newer request owns the state now; let it finish the job.
      if (!isLatest()) return;
      const mappedRoles = mapRolesToNameValue(temp);
      setRoles(mappedRoles);
      // Deliberately unfiltered. GET /projects/{id}/roles already returns only
      // the roles this user may grant — the server builds it from their own
      // INVITE_OWNERS_EDITORS / INVITE_VIEWERS_CONTRIBUTORS_TAGGERS. Filtering
      // again here re-implemented the same rule against the `perms` prop, which
      // this effect does not depend on, so a mount before permissions arrived
      // froze the list empty and the invite dropdown read "No options" for the
      // life of the mount (AKL-848). A second copy of the rule could only ever
      // subtract options the server had already approved.
      setInviteRoles(temp);
    } catch (err) {
      console.error("Error loading roles:", err);
    } finally {
      if (isLatest()) setRolesLoading(false);
    }
  };

  /**
   * Loads the active users, the pending invitations and the grantable roles.
   *
   * The three requests are independent, so they run in parallel. They used to
   * be awaited one after another with the roles last, which on QA left the
   * Invite User button clickable for several seconds before any role existed —
   * the dropdown then read "No options" until the third request came back
   * (AKL-848). Each loader catches its own errors, so one failing does not stop
   * the others.
   */
  const fetchData = async () => {
    try {
      await Promise.all([
        loadActiveUsers(userSearchQuery),
        loadPendingUsers({
          ...userSearchQuery,
          is_verified: false,
          take: null,
          skip: null,
        }),
        loadRoles(),
      ]);
    } catch (err) {
      console.error("Error loading roles:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId, userSearchQuery.role_id, userSearchQuery.skip]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setInviteData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeSearch = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setUserSearchQuery((prevState) => ({
      ...prevState,
      [name]: value,
      skip: 0,
    }));
  };

  const handleSelectRole = (selectedOption: any) => {
    setUserSearchQuery((prevState) => ({
      ...prevState,
      role_id: selectedOption,
      skip: 0,
    }));
  };

  const handleSearch = async () => {
    await loadActiveUsers(userSearchQuery);
    await loadPendingUsers({
      ...userSearchQuery,
      is_verified: false,
      take: null,
      skip: null,
    });
  };

  const handleRoleSelectEnter = async (e: React.KeyboardEvent) => {
    if (userSearchQuery.role_id != null) {
      await loadActiveUsers(userSearchQuery);
      await loadPendingUsers({
        ...userSearchQuery,
        is_verified: false,
        take: null,
        skip: null,
      });
    }
  };

  const handleResendInvitation = async (user_id: string) => {
    try {
      setSendingInvite(true);
      await reinviteProjectUser(projectId, user_id, []);
      toast.success(t("Invitation Resent"));
    } catch (err) {
      toast.error(t("Error Resending Invitation"));
    } finally {
      setSendingInvite(false);
      await loadActiveUsers(userSearchQuery);
      await loadPendingUsers({
        ...userSearchQuery,
        is_verified: false,
        take: null,
        skip: null,
      });
    }
  };

  const handleRemoveInvitation = async (user_id: string) => {
    try {
      setLoading(true);
      await removeUserFromProject(projectId, user_id);
      toast.success(t("User Removed from Project"));
    } catch (err) {
      toast.error(t("Error Removing Invitation"));
    } finally {
      setLoading(false);
      await loadActiveUsers(userSearchQuery);
      await loadPendingUsers({
        ...userSearchQuery,
        is_verified: false,
        take: null,
        skip: null,
      });
    }
  };

  const handleSelectUser = (user_id: number) => {
    // Find the user object based on id
    const user = activeUsers.find((user: any) => user.user_id === user_id);
    if (user !== undefined) {
      setSelectedUser(user);
    }
    // Set the selectedUser as the selected user, or null if not found (this shouldn't really happen if ids are correct)
    setUserActionsModalOpen(true);
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    try {
      setSendingInvite(true);
      await createProjectUser(projectId, inviteData);
      setInviteData({
        email: "",
        role_id: 0,
      });
      setSendingInvite(false);
      toast.success(t("Invite sent successfully!"));
      setInviteModalOpen(false);
      await loadActiveUsers(userSearchQuery);
      await loadPendingUsers({
        ...userSearchQuery,
        is_verified: false,
        take: null,
        skip: null,
      });
    } catch (error: any) {
      console.error("Error sending invite:", error);
      setSendingInvite(false);
      toast.error(t("Error sending invite:") + error.data.detail.toString());
    }
  };

  const handleSelectUsersRole = (selectedOption: any) => {
    setInviteData((prevState) => ({
      ...prevState,
      role_id: selectedOption?.value,
    }));
  };

  const handleClose = () => {
    setInviteModalOpen(false);
    setInviteData({
      email: "",
      role_id: 0,
    });
  };

  const handlePageClick = (e: any) => {
    setUserSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: e.selected * pageSize,
    }));
    localStorage.setItem(
      "projectUsersTableSkip",
      (e.selected * pageSize).toString(),
    );
    setLoading(true);
  };

  return (
    <>
      <ProjectUsersTab
        activeUsers={activeUsers}
        pendingUsers={pendingUsers}
        userSearchQuery={userSearchQuery}
        take={Number(userSearchQuery.take)}
        totalCount={totalActiveUsersCount}
        skip={Number(userSearchQuery.skip)}
        onPageClick={handlePageClick}
        onInviteUser={handleInviteUsers}
        onSelectUser={handleSelectUser}
        isOpen={isUserActionsModalOpen}
        onClose={() => setUserActionsModalOpen(false)}
        selectedUser={selectedUser}
        loading={loading}
        roles={roles}
        projectId={projectId}
        projectName={projectName}
        perms={perms}
        loadUsersAndRoles={fetchData}
        onResendInvitation={handleResendInvitation}
        onRemoveInvitation={handleRemoveInvitation}
        sendingInvite={sendingInvite}
        onChangeSearch={handleChangeSearch}
        onSearch={handleSearch}
        onChangeSelectRole={handleSelectRole}
        handleRoleSelectEnter={handleRoleSelectEnter}
      />
      <InviteUsersModal
        roles={inviteRoles}
        rolesLoading={rolesLoading}
        scope="project"
        inviteData={inviteData}
        isOpen={isInviteModalOpen}
        onSelectUsersRole={handleSelectUsersRole}
        onClose={handleClose}
        onSendInvite={handleSendInvite}
        onChange={handleChange}
        sendingInvite={sendingInvite}
      />
    </>
  );
};

export default ManageProjectUsers;
